import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'
import { useSceneActions } from '@hooks/useScene'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useProjectActions, useProjectState } from '@hooks/useProject'
import Head from 'next/head'

const StudioDynamicComponent = dynamic(
  async () => await import('@components/studio/Studio'),
  {
    ssr: false,
    loading: () => <></>
  }
)

const StudioPage: NextPage = () => {
  const router = useRouter()
  const { uuid } = router.query
  const { setEngineState } = useSceneActions()
  const { detectStudioEnvironment } = useProjectActions()
  const browserCompatibility = useProjectState('browserCompatibility')
  const projectUuid = useProjectState('projectUuid')
  const projectTitle = useProjectState('name')
  const flags = useFlags()
  const allowMultipleStudioEnvironments =
    flags['base-pf-allow-multiple-studio-environments']

  useEffect(() => {
    // Use router.replace bacause if we use router.push, then when the user clicks the back button they will be taken back to the studio, at which point they will be sent back to the compatibility page, ending up in an infinite loop.
    if (browserCompatibility !== 'Compatible') router.replace('/compatibility')
  }, [browserCompatibility])

  // Initialize studio environment on app load
  useEffect(() => {
    if (!projectUuid) return
    // If the app fails to connect to LD then no environment is set and some elements in the studio will not render. To avoid this, default flag values should be set in _app.tsx
    if (typeof allowMultipleStudioEnvironments === 'undefined') return
    detectStudioEnvironment()
  }, [allowMultipleStudioEnvironments, projectUuid])

  useEffect(() => {
    return () => {
      setEngineState('UNMOUNT')
    }
  }, [])

  if (typeof uuid !== 'string') return null
  return (
    <>
      <Head>
        <title>{projectTitle || 'Project Neo Studio'}</title>
        {/* OpenGraph */}
        <meta
          key="og:title"
          property="og:title"
          content={`${projectTitle || 'Project'} | Project Neo (Beta)`}
        />
        <meta
          key="og:description"
          property="og:description"
          content={`Edit ${
            projectTitle || 'your project'
          } in Adobe Project Neo (Beta)`}
        />
        <meta
          key="og:image"
          property="og:image"
          content={`${
            ['local', 'dev'].includes(
              process.env.NEXT_PUBLIC_CLIENT_APP_ENVIRONMENT || ''
            )
              ? 'https://dev.projectneo.adobe.com'
              : process.env.NEXT_PUBLIC_CLIENT_WEB_HOST
          }/storage/projects/${uuid}/images/thumbnail.png`}
        />
        <meta
          key="og:url"
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_CLIENT_WEB_HOST}/studio/${uuid}`}
        />
        <meta
          key="twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
      </Head>
      <StudioDynamicComponent uuid={uuid} />
    </>
  )
}

StudioPage.layout = 'none'
StudioPage.themeProvider = 'none'

export default StudioPage
